import React from "react";

export default function PrivacyPage() {
  const lastUpdated = "2025-12-21";
  const company = "Saham Vista";
  const appName = "Saham Vista";
  const contactEmail = "hai@sahamvista.com";

  const sections = [
    { id: "summary", title: "Ringkasan Singkat" },
    { id: "scope", title: "Ruang Lingkup" },
    { id: "controller", title: "Siapa yang Mengelola Data Anda" },
    { id: "data", title: "Data Pribadi yang Kami Proses" },
    { id: "source", title: "Dari Mana Data Diperoleh" },
    { id: "purpose", title: "Tujuan & Dasar Hukum Pemrosesan" },
    { id: "cookies", title: "Cookie & Teknologi Serupa" },
    { id: "sharing", title: "Penerima Data & Pemroses" },
    { id: "retention", title: "Penyimpanan & Retensi" },
    { id: "rights", title: "Hak-Hak Anda" },
    { id: "transfer", title: "Transfer Lintas Negara" },
    { id: "security", title: "Keamanan" },
    { id: "incident", title: "Pemberitahuan Insiden Data" },
    { id: "changes", title: "Perubahan Kebijakan" },
    { id: "contact", title: "Cara Menghubungi Kami" },
    { id: "definitions", title: "Definisi Ringkas" },
  ];

  return (
    <main className="min-h-screen bg-[#0b0c10] text-gray-200">
      {/* Header */}
      <header className="border-b border-white/10 bg-gradient-to-b from-white/5 to-transparent">
        <div className="mx-auto max-w-5xl px-4 py-10">
          <h1 className="text-3xl font-bold tracking-tight text-purple-300">
            Pemberitahuan Privasi
          </h1>
          <p className="mt-2 text-sm text-gray-400">
            {appName} — Terakhir diperbarui:{" "}
            <time dateTime={lastUpdated}>{lastUpdated}</time>
          </p>
        </div>
      </header>

      {/* Body */}
      <div className="mx-auto max-w-5xl px-4 py-8 lg:grid lg:grid-cols-12 lg:gap-8">
        {/* TOC */}
        <aside className="lg:col-span-4 lg:sticky lg:top-4 lg:self-start">
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <h2 className="mb-3 text-sm font-semibold text-gray-300">
              Daftar Isi
            </h2>
            <nav className="space-y-1">
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="block rounded px-2 py-1 text-sm text-gray-400 hover:bg-white/10 hover:text-white"
                >
                  {s.title}
                </a>
              ))}
            </nav>
            <div className="mt-4 flex items-center gap-2">
              <button
                onClick={() => window.print()}
                className="inline-flex items-center rounded-lg border border-white/10 bg-white/10 px-3 py-1.5 text-xs text-gray-100 hover:bg-white/20"
              >
                Cetak / Simpan PDF
              </button>
              <a
                href="/"
                className="text-xs text-purple-300 hover:underline"
                aria-label="Kembali ke aplikasi"
              >
                Kembali ke aplikasi
              </a>
            </div>
          </div>
        </aside>

        {/* Content */}
        <article className="mt-8 space-y-10 lg:col-span-8 lg:mt-0">
          <Section id="summary" title="Ringkasan Singkat">
            <ul className="list-disc space-y-2 pl-5 text-gray-300">
              <li>
                Login hanya melalui <strong>Google Sign-In</strong>; kami hanya
                menerima <strong>nama</strong> & <strong>alamat email</strong>.
              </li>
              <li>
                Dasar hukum utama: <strong>perjanjian/kontrak layanan</strong>{" "}
                (untuk autentikasi & akses aplikasi); sebagian kecil{" "}
                <strong>kepentingan yang sah</strong> (keamanan & operasi).
              </li>
              <li>Tidak ada cookies analitik/iklan saat ini.</li>
              <li>
                Anda dapat meminta penghapusan akun/data melalui menu atau{" "}
                <a className="underline" href={`mailto:${contactEmail}`}>
                  {contactEmail}
                </a>
                .
              </li>
            </ul>
          </Section>

          <Section id="scope" title="Ruang Lingkup">
            <p>
              Kebijakan ini menjelaskan bagaimana {company} mengumpulkan,
              menggunakan, menyimpan, dan melindungi data pribadi saat Anda
              menggunakan {appName}. Kebijakan ini berlaku untuk versi web
              aplikasi dan layanan terkait.
            </p>
          </Section>

          <Section id="controller" title="Siapa yang Mengelola Data Anda">
            <p>
              <strong>Pengendali Data:</strong> {company}. Untuk pertanyaan
              privasi, hubungi{" "}
              <a className="underline" href={`mailto:${contactEmail}`}>
                {contactEmail}
              </a>
              .
            </p>
          </Section>

          <Section id="data" title="Data Pribadi yang Kami Proses">
            <ul className="list-disc space-y-2 pl-5">
              <li>
                <strong>Nama tampilan Google</strong> – untuk identitas akun.
              </li>
              <li>
                <strong>Alamat email Google</strong> – untuk akun & komunikasi
                penting.
              </li>
              <li className="text-gray-400">
                Opsional/teknis: log keamanan minimal (timestamp, IP, user
                agent) untuk audit keamanan dan pencegahan penyalahgunaan.
                <em> (Non-konten)</em>
              </li>
            </ul>
          </Section>

          <Section id="source" title="Dari Mana Data Diperoleh">
            <p>
              Dari <strong>Google</strong> melalui mekanisme OAuth/Google
              Sign-In saat Anda memberikan izin.
            </p>
          </Section>

          <Section id="purpose" title="Tujuan & Dasar Hukum Pemrosesan">
            <ul className="list-disc space-y-2 pl-5">
              <li>
                <strong>Autentikasi & akses layanan</strong> – membuat/mengelola
                akun, login, dukungan. <em>Dasar hukum: kontrak.</em>
              </li>
              <li>
                <strong>Keamanan & operasional</strong> – pencegahan penyalahgunaan,
                pemantauan kinerja dasar. <em>Dasar hukum: kepentingan yang sah.</em>
              </li>
              <li className="text-gray-400">
                Aktivitas pemasaran/analitik non-esensial akan memerlukan{" "}
                <strong>persetujuan terpisah</strong> jika diterapkan di masa
                depan.
              </li>
            </ul>
          </Section>

          <Section id="cookies" title="Cookie & Teknologi Serupa">
            <p>
              Kami menggunakan <strong>cookie esensial</strong> untuk sesi/login
              agar aplikasi berfungsi. Tidak ada cookie analitik/iklan saat ini.
            </p>
          </Section>

          <Section id="sharing" title="Penerima Data & Pemroses">
            <p>
              Kami dapat menggunakan penyedia infrastruktur (hosting, log) yang
              bertindak sebagai <em>pemroses data</em> berdasarkan perjanjian
              pemrosesan data dan instruksi dari kami. Google bertindak sebagai
              penyedia autentikasi.
            </p>
          </Section>

          <Section id="retention" title="Penyimpanan & Retensi">
            <ul className="list-disc space-y-2 pl-5">
              <li>
                Data akun disimpan <strong>selama akun aktif</strong>.
              </li>
              <li>
                Setelah akun dihapus, data akan dihapus dari sistem aktif
                maksimal <strong>30 hari</strong>. Log teknis minimal dapat
                disimpan hingga <strong>90 hari</strong> untuk keamanan.
              </li>
            </ul>
          </Section>

          <Section id="rights" title="Hak-Hak Anda">
            <p>
              Berdasarkan UU PDP, Anda berhak untuk mengakses, memperbaiki,
              menghapus, membatasi pemrosesan tertentu, menarik persetujuan
              (jika ada), portabilitas data (jika berlaku), serta mengajukan
              keberatan/keluhan. Ajukan permintaan melalui{" "}
              <a className="underline" href={`mailto:${contactEmail}`}>
                {contactEmail}
              </a>
              .
            </p>
          </Section>

          <Section id="transfer" title="Transfer Lintas Negara">
            <p>
              Infrastruktur dan/atau pemroses kami dapat berada di luar
              Indonesia. Kami akan memastikan perlindungan yang memadai sesuai
              hukum yang berlaku.
            </p>
          </Section>

          <Section id="security" title="Keamanan">
            <p>
              Kami menerapkan praktik standar industri (TLS, kontrol akses,
              enkripsi at-rest bila tersedia, least privilege). Meski tidak ada
              sistem yang 100% aman, kami melakukan upaya wajar untuk melindungi
              data Anda.
            </p>
          </Section>

          <Section id="incident" title="Pemberitahuan Insiden Data">
            <p>
              Jika terjadi pelanggaran data yang menimbulkan risiko serius, kami
              akan melakukan pemberitahuan kepada pihak berwenang dan kepada
              Anda sesuai ketentuan (maksimal <strong>3×24 jam</strong> setelah
              diketahui).
            </p>
          </Section>

          <Section id="changes" title="Perubahan Kebijakan">
            <p>
              Kami dapat memperbarui kebijakan ini. Perubahan material akan
              diberitahukan di aplikasi. Tanggal “Terakhir diperbarui” akan
              diperbarui.
            </p>
          </Section>

          <Section id="contact" title="Cara Menghubungi Kami">
            <p>
              Pertanyaan atau permintaan hak subjek data:{" "}
              <a className="underline" href={`mailto:${contactEmail}`}>
                {contactEmail}
              </a>
              .
            </p>
          </Section>

          <Section id="definitions" title="Definisi Ringkas">
            <ul className="list-disc space-y-2 pl-5">
              <li>
                <strong>Data Pribadi:</strong> data tentang orang perorangan
                yang teridentifikasi atau dapat diidentifikasi.
              </li>
              <li>
                <strong>Pengendali Data:</strong> pihak yang menentukan tujuan
                dan melakukan kontrol atas pemrosesan data.
              </li>
              <li>
                <strong>Pemroses Data:</strong> pihak yang memproses data atas
                nama pengendali.
              </li>
            </ul>
          </Section>

          <div className="border-t border-white/10 pt-6 text-sm text-gray-400">
            © {new Date().getFullYear()} {company}. Semua hak dilindungi.
          </div>
        </article>
      </div>

      {/* small top offset so anchor links don't hide behind header */}
      <style>{`
        :target { scroll-margin-top: 96px; }
        @media print {
          aside { display: none; }
          header { border: none; background: transparent; }
          main { color: #000; }
        }
      `}</style>
    </main>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="mb-3 text-xl font-semibold text-white">{title}</h2>
      <div className="prose prose-invert max-w-none prose-p:leading-relaxed prose-li:leading-relaxed">
        {children}
      </div>
    </section>
  );
}
